import type { ClientGeoAreaAncestor } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { BottomSheetModal } from '@gorhom/bottom-sheet';
import type { FC } from 'react';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { LayoutChangeEvent } from 'react-native';
import { StyleSheet } from 'react-native';
import Animated, { useAnimatedStyle } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useAppState } from '@/features/authorization/provider/useAppState';
import { RegionLocationBar } from '@/features/geo-area/components/region/RegionLocationBar';
import { getRegionTabs } from '@/features/geo-area/components/region/regionOptions';
import { RegionPageTabs } from '@/features/geo-area/components/region/RegionPageTabs';
import { RegionPartnersSheet } from '@/features/geo-area/components/region/RegionPartnersSheet';
import { useRegionTab } from '@/features/geo-area/components/region/RegionTab';
import type { RegionTab } from '@/features/geo-area/components/region/regionTypes';
import type { GenericPostDetail } from '@/features/posts/types/posts';
import { countNewPosts } from '@/features/posts/utils/newPosts';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { useBadgeStore } from '@/shared/store/useBadgeStore';
import { isNative } from '@/shared/utils/platform';

type Props = {
  ancestors?: ClientGeoAreaAncestor[] | null;
  emergencyPosts?: GenericPostDetail[];
  onTabChange: (tab: RegionTab) => void;
};

export const MeineRegionHeader: FC<Props> = ({
  ancestors,
  emergencyPosts,
  onTabChange
}) => {
  const { activeTab, scrollY, locationBarHeight, setLocationBarHeight } =
    useRegionTab();
  const insets = useSafeAreaInsets();
  const topInset = isNative ? insets.top : 0;
  const { t } = useTranslation(LocalizationNameSpace.GEO_AREA, {
    keyPrefix: 'screens.myRegion'
  });
  const hasDeepAncestors = (ancestors?.length ?? 0) > 2;
  const regionAncestor = hasDeepAncestors ? ancestors?.at(2) : ancestors?.at(1);
  const parentAncestor = ancestors?.at(1);
  const region = regionAncestor?.name;
  const subtitle = hasDeepAncestors ? parentAncestor?.name : undefined;
  const partnersAncestor = hasDeepAncestors ? parentAncestor : regionAncestor;
  const partnersGeoAreaId = partnersAncestor?.id;
  const partnersName = partnersAncestor?.name;

  const partnersSheetRef = useRef<BottomSheetModal>(null);
  const onHeaderPress = partnersGeoAreaId
    ? () => partnersSheetRef.current?.present()
    : undefined;

  const readPostBadgeEntries = useBadgeStore(
    (state) => state.readPostBadgeEntries
  );
  const { userId } = useAppState();
  const hasNewEmergency = useMemo(
    () => countNewPosts(emergencyPosts, readPostBadgeEntries, userId) > 0,
    [emergencyPosts, readPostBadgeEntries, userId]
  );
  const regionTabs = useMemo(
    () => getRegionTabs(t, hasNewEmergency),
    [t, hasNewEmergency]
  );

  const headerStyle = useAnimatedStyle(() => {
    'worklet';
    return {
      transform: [
        {
          translateY: -Math.min(Math.max(scrollY.value, 0), locationBarHeight)
        }
      ]
    };
  });

  const onLocationBarLayout = useCallback(
    (event: LayoutChangeEvent) => {
      setLocationBarHeight(event.nativeEvent.layout.height);
    },
    [setLocationBarHeight]
  );

  return (
    <>
      <Animated.View style={[styles.header, { top: topInset }, headerStyle]}>
        <RegionLocationBar
          region={region}
          subtitle={subtitle}
          onPress={onHeaderPress}
          onLayout={onLocationBarLayout}
        />
        <RegionPageTabs
          tabs={regionTabs}
          value={activeTab}
          onChange={onTabChange}
        />
      </Animated.View>
      <RegionPartnersSheet
        ref={partnersSheetRef}
        geoAreaId={partnersGeoAreaId}
        regionName={partnersName}
      />
    </>
  );
};

const styles = StyleSheet.create({
  header: {
    position: 'absolute',
    left: 0,
    right: 0,
    zIndex: 2
  }
});
